// 議事録詳細 — Claude Design プロトタイプ (app/MeetingDetail.jsx) のダーク基調に合わせた版。
// 実データは単一の Markdown 議事録ドキュメント（projects/{id}/meetings/{mid}/minutes）なので、
// プロトタイプの音声プレイヤー / 文字起こし / 懸案タブは持たず、ヘッダ + 本文カード + 編集/出力を
// 同じデザイントークンで表現する。
import React, { useEffect, useRef, useState } from 'react';
import { User } from 'firebase/auth';
import { collection, getDocs, doc, onSnapshot, updateDoc, serverTimestamp, orderBy, query } from 'firebase/firestore';
import ReactMarkdown from 'react-markdown';
import { db } from '../firebase';
import { Btn, Card, Icon, LockVeil, Pill } from '../components/primitives';
import { useCompanies } from '../useCompanies';
import { usePlan } from '../usePlan';
import { canViewMeeting, planHasFullTimeline, webDisplayPlan } from '../plan';
import { useFreeTimelineViewLimit } from '../remoteConfig';

interface Meeting {
  id: string;
  title: string;
  recordedAt?: any;
  duration?: number;
  channel?: string;
  status?: string;
  companyIds?: string[];
}

interface Project {
  id: string;
  name: string;
}

interface Minutes {
  id: string;
  content: string;
  generatedAt?: any;
  updatedAt?: any;
}

interface Props {
  user: User;
  project: Project;
  meeting: Meeting;
  onBack: () => void;
  // S-058-7: まとめの引用タップ由来の逐語。本文中で該当箇所をスクロール＆強調する（任意・一致しなければ無視）。
  highlight?: string;
  // Web で議事録の中身を閲覧できないプラン（free / light / DEC-059）。本文だけをモザイク化し、
  // ヘッダ（タイトル・日時・相手先）は読める状態のまま、本文上に小さなアップグレード誘導を重ねる。
  webMinutesLocked?: boolean;
}

// 本文 DOM から逐語に一致するテキストノードを探し、スクロール＆一時ハイライトする。
// Markdown は要約文・highlight は逐語引用なので完全一致しないことも多い → 段階的に短縮して試し、
// 見つからなければ何もしない（グレースフル・DoD「一致しないときも壊れない」）。
function highlightEvidence(container: HTMLElement, raw: string): boolean {
  const cleaned = raw.replace(/[「」“”"』『]/g, '').trim();
  if (cleaned.length < 4) return false;
  const candidates = Array.from(
    new Set([cleaned, cleaned.slice(0, 24), cleaned.slice(0, 14)].filter((c) => c.length >= 4))
  );
  for (const cand of candidates) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    // eslint-disable-next-line no-cond-assign
    while ((node = walker.nextNode())) {
      const text = node.textContent || '';
      const idx = text.indexOf(cand);
      if (idx === -1) continue;
      try {
        const range = document.createRange();
        range.setStart(node, idx);
        range.setEnd(node, idx + cand.length);
        const mark = document.createElement('mark');
        mark.style.background = 'var(--cta-fill)';
        mark.style.color = 'inherit';
        mark.style.borderRadius = '3px';
        mark.style.boxShadow = '0 0 0 2px var(--cta-line)';
        mark.style.padding = '0 1px';
        range.surroundContents(mark);
        mark.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return true;
      } catch {
        return false; // surroundContents が境界をまたぐ等 → 諦める（壊さない）
      }
    }
  }
  return false;
}

const toDate = (ts: any): Date | null => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
};

const MinutesDetailPage: React.FC<Props> = ({ user, project, meeting, onBack, highlight, webMinutesLocked }) => {
  const [minutes, setMinutes] = useState<Minutes | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const highlightedRef = useRef(false);

  // ─── DEC-059: 無料プランの議事録閲覧ゲート ───
  // free は「案件内 直近 N 件」の議事録しか閲覧できない。タイムラインのロックカードを
  // 迂回して（未解決アクション/約束カード/まとめの出典リンク、ブラウザ直リンク・履歴 等）
  // 詳細ページに辿り着いても、本ページが最終防衛線として超過分をブロックする。
  // light/standard は常に許可。plan/件数解決前は 'checking'（本文を取得・描画しない）。
  const { plan, loading: planLoading } = usePlan(user.uid);
  const freeViewLimit = useFreeTimelineViewLimit();
  // 'gateError' は本文を読めない安全側状態だが、超過ブロックとは区別する
  // （通信エラー時に無料の正規ユーザーへ誤って課金訴求しないため）。
  const [access, setAccess] = useState<'checking' | 'allowed' | 'blocked' | 'gateError'>('checking');

  useEffect(() => {
    let cancelled = false;
    // プラン解決待ちは判定保留（本文を読まない）。楽観アンロックはしない＝本文の一瞬の露出を防ぐ。
    if (planLoading) {
      setAccess('checking');
      return;
    }
    // 有料（light/standard）は案件の会議一覧を読みに行かずに即許可（課金者の余計な read を増やさない）。
    // Web 表示では free も light と同一にする（webDisplayPlan）＝ free も詳細を開ける（中身は
    // webMinutesLocked によりモザイク）。これにより free の超過ブロックページは出ず light と揃う。
    if (planHasFullTimeline(webDisplayPlan(plan))) {
      setAccess('allowed');
      return;
    }
    // free: 案件内の非削除会議を recordedAt 降順で取得し、対象会議が直近 N 件に入るか判定する。
    setAccess('checking');
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'projects', project.id, 'meetings'), orderBy('recordedAt', 'desc'))
        );
        if (cancelled) return;
        const idsDesc = snap.docs
          .filter((d) => (d.data() as any).deletedAt == null)
          .map((d) => d.id);
        setAccess(canViewMeeting(webDisplayPlan(plan), idsDesc, meeting.id, freeViewLimit) ? 'allowed' : 'blocked');
      } catch (e) {
        // 会議一覧の取得に失敗（オフライン/権限）。議事録本文も同じ rules で読めないため
        // 本文は見せない（安全側）。ただし超過ブロックではなく 'gateError' として
        // 再読み込み導線を出す（無料の正規ユーザーへ誤って課金訴求しない）。
        console.error('[MinutesDetailPage] view-gate meeting list fetch failed:', e);
        if (!cancelled) setAccess('gateError');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [planLoading, plan, freeViewLimit, project.id, meeting.id]);

  // DEC-051: 相手先（取引先）の1タップ訂正。meeting.companyIds をライブ購読し、
  // 取引先マスタで名前解決してラベル表示・付替え・削除を行う。
  const { companies, companyMap } = useCompanies(user.uid);
  const [companyIds, setCompanyIds] = useState<string[]>(meeting.companyIds || []);
  const [editingCompanies, setEditingCompanies] = useState(false);
  const [savingCompanies, setSavingCompanies] = useState(false);

  useEffect(() => {
    const ref = doc(db, 'projects', project.id, 'meetings', meeting.id);
    const unsubscribe = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const ids = (snap.data() as any).companyIds;
        setCompanyIds(Array.isArray(ids) ? ids : []);
      }
    }, (e) => console.error('[MinutesDetailPage] meeting subscribe failed:', e));
    return unsubscribe;
  }, [project.id, meeting.id]);

  const persistCompanyIds = async (next: string[]) => {
    const prev = companyIds;
    setCompanyIds(next);
    setSavingCompanies(true);
    try {
      const ref = doc(db, 'projects', project.id, 'meetings', meeting.id);
      // S-063-18 (AR4/AR5): 手で直した相手先は遡及更新・自動補完の保護対象にする。
      //   meta.manualEditedFields.companyIds を立て、applyCalendarMatchApproval が上書きをスキップする。
      await updateDoc(ref, {
        companyIds: next,
        routingSource: 'manual',
        'meta.manualEditedFields.companyIds': true,
      });
    } catch (e: any) {
      console.error('[MinutesDetailPage] failed to update companyIds:', e);
      setCompanyIds(prev);
      alert('相手先の保存に失敗しました: ' + (e.message || e));
    } finally {
      setSavingCompanies(false);
    }
  };
  const removeCompany = (id: string) => persistCompanyIds(companyIds.filter((c) => c !== id));
  const addCompany = (id: string) => {
    if (companyIds.includes(id)) return;
    persistCompanyIds([...companyIds, id]);
  };

  useEffect(() => {
    // DEC-059: 閲覧許可が出る前は本文を取得しない（無料超過分は読み込み自体を行わない）。
    if (access !== 'allowed') return;
    const fetchMinutes = async () => {
      try {
        const snapshot = await getDocs(
          collection(db, 'projects', project.id, 'meetings', meeting.id, 'minutes')
        );
        if (!snapshot.empty) {
          const d = snapshot.docs[0];
          setMinutes({ id: d.id, ...d.data() } as Minutes);
        }
      } catch (e: any) {
        setError('議事録の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    fetchMinutes();
  }, [user.uid, project.id, meeting.id, access]);

  // S-058-7: 議事録本文がレンダーされた後に、まとめ由来の逐語をスクロール＆強調する（1回だけ）。
  useEffect(() => {
    if (!highlight || !minutes || editMode || highlightedRef.current) return;
    const el = contentRef.current;
    if (!el) return;
    const id = window.setTimeout(() => {
      if (contentRef.current) {
        highlightEvidence(contentRef.current, highlight);
        highlightedRef.current = true;
      }
    }, 120);
    return () => window.clearTimeout(id);
  }, [highlight, minutes, editMode]);

  const handleEdit = () => {
    setEditContent(minutes?.content || '');
    setEditMode(true);
  };
  const handleCancel = () => {
    setEditMode(false);
    setEditContent('');
  };
  const handleSave = async () => {
    if (!minutes) return;
    setSaving(true);
    try {
      const minutesRef = doc(db, 'projects', project.id, 'meetings', meeting.id, 'minutes', minutes.id);
      await updateDoc(minutesRef, { content: editContent, updatedAt: serverTimestamp() });
      setMinutes({ ...minutes, content: editContent });
      setEditMode(false);
    } catch (e: any) {
      alert('保存に失敗しました: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  // 出力: 議事録 Markdown を .md として書き出す（実機能）。
  const handleExport = () => {
    if (!minutes) return;
    const blob = new Blob([minutes.content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(meeting.title || '議事録').replace(/[\\/:*?"<>|]/g, '_')}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatDateTime = (ts: any) => {
    const d = toDate(ts);
    if (!d) return '';
    return d.toLocaleString('ja-JP', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  const formatDuration = (seconds?: number) => {
    if (!seconds) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}分${s > 0 ? s + '秒' : ''}`;
  };

  const goPricing = () => { window.location.href = '/pricing'; };

  const BackButton = (
    <button onClick={onBack} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
      color: 'var(--fg-tertiary)', font: 'var(--type-ui)', cursor: 'pointer', marginBottom: 16, padding: 0,
    }}>
      <Icon name="arrow-left" size={16} />ダッシュボードに戻る
    </button>
  );

  // DEC-059 ゲート: 判定中は本文を出さずローディング。超過（blocked）はアップグレード誘導に差し替える。
  if (access !== 'allowed') {
    return (
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '20px 24px 96px' }}>
        {BackButton}
        {access === 'checking' ? (
          <div style={{ textAlign: 'center', color: 'var(--fg-tertiary)', padding: 48, font: 'var(--type-body)' }}>読み込み中...</div>
        ) : access === 'gateError' ? (
          <Card style={{ textAlign: 'center', padding: 40 }}>
            <Icon name="wifi-off" size={26} style={{ color: 'var(--fg-tertiary)', marginBottom: 10 }} />
            <p style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)', marginBottom: 14 }}>
              議事録を読み込めませんでした。電波のある場所でもう一度お試しください。
            </p>
            <Btn kind="ghost" size="sm" icon="refresh-cw" onClick={() => window.location.reload()}>再読み込み</Btn>
          </Card>
        ) : (
          <Card pad={22} style={{ borderStyle: 'dashed' }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{
                width: 40, height: 40, borderRadius: 999, flexShrink: 0, display: 'grid', placeItems: 'center',
                background: 'var(--cta-fill)', color: 'var(--cta)',
              }}>
                <Icon name="lock" size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <h2 style={{ font: 'var(--type-h2)', color: 'var(--fg)', marginBottom: 8 }}>
                  この議事録はライト以上で閲覧できます
                </h2>
                <p style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)', lineHeight: 1.7, marginBottom: 16 }}>
                  無料プランで閲覧できるのは直近{freeViewLimit}件までです。<br />
                  ライト（¥480/月）で過去の議事録もすべて閲覧でき、スタンダード（¥980/月）ではAIにも聞けます。
                </p>
                <Btn kind="cta" icon="arrow-up-circle" onClick={goPricing}>プランを見る</Btn>
              </div>
            </div>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '20px 24px 96px' }}>
      {BackButton}

      {/* ヘッダ */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 22, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h1 style={{ font: 'var(--type-h2)', color: 'var(--fg)', marginBottom: 8 }}>{meeting.title || '無題の打ち合わせ'}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            {meeting.recordedAt && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
                <Icon name="calendar" size={13} />{formatDateTime(meeting.recordedAt)}
              </span>
            )}
            {meeting.duration ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
                <Icon name="clock" size={13} />{formatDuration(meeting.duration)}
              </span>
            ) : null}
            {meeting.status === 'completed' && <Pill kind="success">完了</Pill>}
          </div>

          {/* DEC-051: 相手先（自動で整理）。タップで付替え/削除/追加。 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>
              <Icon name="building" size={13} />相手先
            </span>
            {companyIds.length === 0 && !editingCompanies && (
              <span style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>未分類</span>
            )}
            {companyIds.map((id) => {
              const name = companyMap[id]?.name || '（不明な相手先）';
              return (
                <span key={id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px',
                  borderRadius: 'var(--r-pill)', background: 'var(--accent-fill)',
                  border: '1px solid var(--border-accent)', color: 'var(--teal-300)',
                  font: 'var(--type-caption)', fontWeight: 500,
                }}>
                  {name}
                  <button onClick={() => removeCompany(id)} disabled={savingCompanies}
                    title="この相手先を外す"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', color: 'var(--teal-300)' }}>
                    <Icon name="x" size={12} />
                  </button>
                </span>
              );
            })}
            <button onClick={() => setEditingCompanies((v) => !v)} disabled={savingCompanies}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px',
                borderRadius: 'var(--r-pill)', background: 'transparent',
                border: '1px dashed var(--border-strong)', color: 'var(--fg-secondary)',
                font: 'var(--type-caption)', fontWeight: 500, cursor: 'pointer',
              }}>
              <Icon name={editingCompanies ? 'check' : 'align-left'} size={12} />
              {editingCompanies ? '完了' : '変更'}
            </button>
          </div>

          {/* 訂正パレット: マスタの相手先から付与する */}
          {editingCompanies && (
            <div style={{ marginTop: 10, padding: 12, borderRadius: 'var(--r-2)', background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
              {companies.length === 0 ? (
                <p style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', margin: 0 }}>
                  取引先がまだありません。議事録を作成すると自動で追加されます。
                </p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {companies.map((c) => {
                    const active = companyIds.includes(c.id);
                    return (
                      <button key={c.id} onClick={() => (active ? removeCompany(c.id) : addCompany(c.id))} disabled={savingCompanies}
                        style={{
                          padding: '4px 11px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
                          background: active ? 'var(--accent-fill)' : 'transparent',
                          border: '1px solid ' + (active ? 'var(--border-accent)' : 'var(--border-strong)'),
                          color: active ? 'var(--teal-300)' : 'var(--fg-secondary)',
                          font: 'var(--type-caption)', fontWeight: 500,
                        }}>
                        {active ? '✓ ' : ''}{c.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        {/* Web 閲覧不可プラン（free / light）では編集・出力を出さない
            （編集モードは textarea に生本文を露出するため＝モザイクの抜け穴になる）。 */}
        {!editMode && minutes && !webMinutesLocked && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn kind="ghost" size="sm" icon="download" onClick={handleExport}>出力</Btn>
            <Btn kind="accent" size="sm" icon="align-left" onClick={handleEdit}>編集</Btn>
          </div>
        )}
      </div>

      {loading && <div style={{ textAlign: 'center', color: 'var(--fg-tertiary)', padding: 48, font: 'var(--type-body)' }}>読み込み中...</div>}
      {error && (
        <div style={{ padding: '12px 16px', borderRadius: 'var(--r-2)', background: 'var(--danger-fill)', border: '1px solid var(--danger-line)', color: 'var(--danger)', font: 'var(--type-caption)' }}>
          {error}
        </div>
      )}

      {!loading && !error && !minutes && (
        <Card style={{ textAlign: 'center', padding: 48 }}>
          <Icon name="align-left" size={28} style={{ color: 'var(--fg-tertiary)', marginBottom: 10 }} />
          <p style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)' }}>議事録がありません</p>
        </Card>
      )}

      {/* Web 閲覧不可プラン（free / light / DEC-059）: 本文だけをモザイク化し、
          上に小さなアップグレード誘導を重ねる（「議事録はあるが読めない」を直感的に伝える）。 */}
      {minutes && !editMode && webMinutesLocked && (
        <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 'var(--r-3)' }}>
          <div aria-hidden style={{ filter: 'blur(7px) saturate(0.85)', pointerEvents: 'none', userSelect: 'none' }}>
            <Card>
              <div className="markdown-content" style={{ lineHeight: 1.8, color: 'var(--fg-secondary)', fontSize: 15, overflowWrap: 'break-word' }}>
                <ReactMarkdown>{minutes.content}</ReactMarkdown>
              </div>
            </Card>
          </div>
          <LockVeil
            icon="lock"
            message="議事録の本文はスタンダードプラン（¥980/月）で閲覧できます"
            cta="プランを見る"
            onUpsell={goPricing}
          />
        </div>
      )}

      {minutes && !editMode && !webMinutesLocked && (
        <Card>
          <div ref={contentRef} className="markdown-content" style={{ lineHeight: 1.8, color: 'var(--fg-secondary)', fontSize: 15, overflowWrap: 'break-word' }}>
            <ReactMarkdown>{minutes.content}</ReactMarkdown>
          </div>
          {minutes.updatedAt && (
            <p style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              最終更新: {formatDateTime(minutes.updatedAt)}
            </p>
          )}
        </Card>
      )}

      {editMode && (
        <Card>
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            placeholder="議事録をMarkdown形式で入力..."
            style={{
              width: '100%', minHeight: 400, borderRadius: 'var(--r-2)', padding: 14,
              background: 'var(--bg-hover)', border: '1px solid var(--border-strong)',
              color: 'var(--fg)', font: 'var(--type-mono)', fontSize: 13.5, lineHeight: 1.7,
              resize: 'vertical', boxSizing: 'border-box', outline: 'none',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <Btn kind="ghost" size="sm" onClick={handleCancel} disabled={saving}>キャンセル</Btn>
            <Btn kind="cta" size="sm" icon={saving ? 'loader' : 'check'} iconSpin={saving} onClick={handleSave} disabled={saving}>
              {saving ? '保存中...' : '保存'}
            </Btn>
          </div>
        </Card>
      )}
    </div>
  );
};

export default MinutesDetailPage;
